import { type ReactNode } from "react";

type Props = {
  active?: boolean;
  number?: string;
  label: ReactNode;
  caption?: string;
  onClick?: () => void;
  className?: string;
};

// Vertical ticket stub with perforated edges. Used for category nav.
export function TicketStub({ active, number, label, caption, onClick, className = "" }: Props) {
  return (
    <button
      onClick={onClick}
      className={`relative w-full text-left transition active:scale-[0.97] ${className}`}
    >
      <div
        className={`relative px-4 py-4 rounded-sm transition ${
          active ? "bg-mezcal text-paper" : "bg-paper text-ink hover:bg-sal/60"
        }`}
        style={{
          // Perforated left + right edges: two columns of dots
          backgroundImage: active
            ? "radial-gradient(circle at 8px center, transparent 4px, currentColor 4.5px, currentColor 5px, transparent 5.5px)"
            : "none",
          backgroundSize: "16px 16px",
          backgroundRepeat: "repeat-y",
          backgroundPosition: "left center, right center",
        }}
      >
        <div className="font-mono text-[10px] tracking-[0.2em] uppercase opacity-70">{number}</div>
        <div className="font-display font-bold text-2xl leading-none mt-0.5">{label}</div>
        {caption && <div className="font-sans text-xs opacity-70 mt-1">{caption}</div>}
        {/* notches on left/right */}
        <span className="absolute -left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-ink" />
        <span className="absolute -right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-ink" />
      </div>
    </button>
  );
}

// Horizontal progress stubs: 4 connected stubs, current + previous "punched"
export function ProgressStubs({ total, current }: { total: number; current: number }) {
  return (
    <div className="flex gap-1">
      {Array.from({ length: total }).map((_, i) => {
        const punched = i <= current;
        return (
          <div key={i} className="relative w-14 h-8 bg-paper/10 border border-paper/30 rounded-sm overflow-hidden">
            {punched && (
              <div className="absolute inset-1 rounded-full border-2 border-dashed border-mango/80 flex items-center justify-center">
                <span className="text-mango text-xs font-mono">✕</span>
              </div>
            )}
            {/* perforation */}
            {i < total - 1 && (
              <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-2 h-full flex flex-col justify-around">
                <span className="w-0.5 h-1 bg-paper/40 rounded-full" />
                <span className="w-0.5 h-1 bg-paper/40 rounded-full" />
                <span className="w-0.5 h-1 bg-paper/40 rounded-full" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
